import { createFeatureSelector, createSelector } from '@ngrx/store';
import { get as _get } from 'lodash';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { BillAccountsSelectors as fromBillAccounts } from '@amfam/billing/billaccount/data-access';
import { pendingPaymentQuery } from '@amfam/billing/pending-payment/data-access';
import { PolicySelectors as fromPolicies } from '@amfam/policy/data-access';
import { EdeliverySelectors as fromEdeliverySelectors } from '@amfam/policy/e-delivery/data-access';
import { GoPaperLessSelectors as fromGoPaperless } from '@amfam/policy/go-paperless/data-access';
import { Policy, SourceSystemType } from '@amfam/policy/models';
import { profileQuery } from '@amfam/profile/data-access';
import { DeliveryPreferences } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';

import * as fromBillingSharedFeature from './common.reducer';
import { billingSharedFeatureKey, BillingSharedFeatureState } from './common.reducer';

const selectBillingSharedFeatureState =
  createFeatureSelector<BillingSharedFeatureState>(billingSharedFeatureKey);
/**
 * AS: This reducer will return the list of billaccounts eligible for setting up
 * autopay
 */

export const selectBillaccountsListEligible = createSelector(
  fromBillAccounts.selectModifiableBillAccounts,
  AutoPaySelectors.getAutomaticPayments,
  AutoPaySelectors.getAutoPayNotificationEntities,
  (billaccounts, automaticPayments, autopayNotificationEntities) =>
    billaccounts.filter(
      billaccount =>
        !automaticPayments.find(
          automaticPayment =>
            automaticPayment?.billAccountNumber === billaccount.billAccountNumber &&
            !autopayNotificationEntities[automaticPayment.billAccountNumber].hasError
        )
    )
);

// filter eligible billaccounts and check if the property policies has TPI.
export const selectBillaccountsListEligibleForAutopay = createSelector(
  fromPolicies.selectPropertyPoliciesWithTPI,
  selectBillaccountsListEligible,
  (policies, billaccounts) =>
    billaccounts.filter(
      billaccount =>
        !policies.find(
          homePolicy => homePolicy.billingAccountNumber === billaccount.billAccountNumber
        )
    )
);

// filter  billaccounts and return billaccounts with property policies has TPI.
export const selectBillaccountsListNotEligibleForAutopay = createSelector(
  fromPolicies.selectPropertyPoliciesWithTPI,
  selectBillaccountsListEligible,
  (policies, billaccounts) =>
    billaccounts.filter(billaccount =>
      policies.find(homePolicy => homePolicy.billingAccountNumber === billaccount.billAccountNumber)
    )
);

export const selectPendingPaymentBillAccounts = createSelector(
  pendingPaymentQuery.selectPendingPayments,
  fromBillAccounts.selectBillAccounts,
  (pendingPayments, billAccounts) =>
    billAccounts.filter(ba => {
      const matchingBillAccount = pendingPayments.find(
        pendingPayment => pendingPayment.billAccountNumber === ba.billAccountNumber
      );
      return !!matchingBillAccount;
    })
);

export const selectIsAnyAdvancedPoliciyInGivenPolicies = createSelector(
  fromPolicies.selectPolicies,
  (policies: Policy[], data: { dataPolicies: Policy[] }) => {
    let isAdvancedPolicy = false;
    data.dataPolicies.forEach(policy => {
      if (!isAdvancedPolicy) {
        const foundPolicy = policies.find(p => p.policyNumber === policy.policyNumber);

        if (foundPolicy && foundPolicy.sourceSystem === SourceSystemType.Advance) {
          isAdvancedPolicy = true;
        }
      }
    });
    return isAdvancedPolicy;
  }
);

/**
 * @returns boolean.
 * Conditions to be satisfied to display ez button.
 * 1. Risks are available
 * 2. No terms and conditions error
 * 3. Atleast one billaccount/policy preference is not email
 * 4. No only life policies
 * 5. No retrieve preferences error
 */

export const selectIsEligibleToGoPaperless = createSelector(
  fromBillAccounts.selectBillAccounts,
  fromGoPaperless.selectIsEnrolledGoPaperless,
  fromPolicies.selectPolicyRisks,
  fromEdeliverySelectors.getEdeliveryTermsAndConditionsHasError,
  fromPolicies.selectIsEligibleForPaperless,
  fromBillAccounts.selectIsEligibleToViewPreferences,
  (
    billAccounts,
    isPolicyPaperless,
    risks,
    eDeliveryTermsAndConditionsError,
    nonLifePolicies,
    noPreferencesError
  ) => {
    const isPolicyPaper = !isPolicyPaperless;
    const isBillingPaper = !!billAccounts.find(billAccount =>
      _get(billAccount, 'billingPreferences.preferences', []).some(
        preference =>
          preference.isModifiable &&
          preference.preferenceDeliveryCode.toLowerCase() !== DeliveryPreferences.EMAIL
      )
    );
    return (
      risks.length > 0 &&
      !eDeliveryTermsAndConditionsError &&
      (isBillingPaper || isPolicyPaper) &&
      nonLifePolicies &&
      noPreferencesError
    );
  }
);

export const selectUpdatedMyAccountEmailPayload = createSelector(
  profileQuery.selectUpdatedMyAccountEmailAddress,
  userQuery.selectEmails,
  (updatedEmailAddress, emails) => emails.find(email => email.emailAddress === updatedEmailAddress)
);

export const selectProfileCardData = createSelector(
  userQuery.selectAddress,
  userQuery.selectFirstName,
  userQuery.selectMyAccountEmail,
  selectUpdatedMyAccountEmailPayload,
  userQuery.selectPrimaryPhone,
  userQuery.selectLoginName,
  (address, first, email, updatedEmail, phone, loginName) => ({
    address: address,
    firstName: first,
    primaryEmail: updatedEmail ? updatedEmail : email,
    primaryPhone: phone,
    userId: loginName
  })
);

export const selectAddPaymentMethodFailureCount = createSelector(
  selectBillingSharedFeatureState,
  (state: fromBillingSharedFeature.BillingSharedFeatureState) => state.addPaymentMethodFailureCount
);

export const billingSharedSelectors = {
  selectBillaccountsListEligible,
  selectBillaccountsListEligibleForAutopay,
  selectBillaccountsListNotEligibleForAutopay,
  selectPendingPaymentBillAccounts,
  selectIsAnyAdvancedPoliciyInGivenPolicies,
  selectIsEligibleToGoPaperless,
  selectUpdatedMyAccountEmailPayload,
  selectProfileCardData,
  selectAddPaymentMethodFailureCount
};
